import { useDiscardChangesGuard } from '@components/v2/ui/DiscardChangesModal/hooks/useDiscardChangesGuard';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { BANNER_UPLOAD_WIDTH } from '../../../components/Site/BannerForm/bannerForm.constants';
import useImageUpload from '../../../hooks/useImageUpload';
import useMoney from '../../../hooks/useMoney';
import LangEnum from '../../../lang/LangEnum';
import { useSaveShowroomAboutMutation } from '../../../services/modules/brand-showroom/brand-showroom.mutations';
import { BrandShowroom } from '../../../services/modules/brand-showroom/brand-showroom.types';
import { AveragePriceOption, WholesaleMediumPricesTypeEnum } from '../../../types/Enums';
import useSegmentOptions from '../../segments/hooks/useSegmentOptions';
import { useTranslation } from '../../translation/TranslationContext';
import {
  BrandAboutSectionFormValues,
  buildBrandAboutSectionSchema,
} from '../brandAboutSectionForm.schema';
import { buildBrandAboutPayload } from '../brandAboutSectionForm.utils';

const toText = (value?: string | null) => value ?? '';

interface UseBrandAboutSectionFormParams {
  isOpen: boolean;
  initialData: Partial<BrandShowroom> | undefined;
  onSave: () => void;
  onClose: () => void;
}

const buildDefaultValues = (
  initialData: Partial<BrandShowroom> | undefined,
): BrandAboutSectionFormValues => ({
  commercialName: toText(initialData?.commercialName),
  title: toText(initialData?.title),
  website: toText(initialData?.website),
  aboutMe: toText(initialData?.aboutMe),
  wholesaleMediumPrice: toText(initialData?.wholesaleMediumPrice),
  logoUrl: initialData?.logoUrl ?? null,
  bannerUrl: initialData?.bannerUrl ?? null,
  instagramUrl: toText(initialData?.instagramUrl),
  facebookUrl: toText(initialData?.facebookUrl),
  twitterUrl: toText(initialData?.twitterUrl),
  pinterestUrl: toText(initialData?.pinterestUrl),
  youtubeUrl: toText(initialData?.youtubeUrl),
  segmentIds: initialData?.segments?.map((segment) => segment.id) ?? [],
});

const useBrandAboutSectionForm = ({
  isOpen,
  initialData,
  onSave,
  onClose,
}: UseBrandAboutSectionFormParams) => {
  const { translate } = useTranslation();
  const segmentOptions = useSegmentOptions({ isEnabled: isOpen });
  const { formatMoney } = useMoney();
  const bannerUpload = useImageUpload();
  const logoUpload = useImageUpload();

  const form = useForm<BrandAboutSectionFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(buildBrandAboutSectionSchema(translate)),
    values: buildDefaultValues(initialData),
    resetOptions: { keepDirtyValues: true },
  });

  const { isPending: isSaving, saveAbout } = useSaveShowroomAboutMutation({ onSuccess: onSave });

  const bannerUrl = useWatch({ control: form.control, name: 'bannerUrl' });
  const logoUrl = useWatch({ control: form.control, name: 'logoUrl' });
  const segmentIds = useWatch({ control: form.control, name: 'segmentIds' });

  const handleChangeBanner = async (file?: File) => {
    if (!file) {
      form.setValue('bannerUrl', null, { shouldDirty: true });
      return;
    }

    const newBannerUrl = await bannerUpload.uploadImage(file, BANNER_UPLOAD_WIDTH);

    if (newBannerUrl) {
      form.setValue('bannerUrl', newBannerUrl, { shouldDirty: true });
    }
  };

  const handleChangeLogo = async (file: File) => {
    const newLogoUrl = await logoUpload.uploadImage(file);

    if (newLogoUrl) {
      form.setValue('logoUrl', newLogoUrl, { shouldDirty: true });
    }
  };

  const handleToggleSegment = (segmentId: string) => {
    const nextSegmentIds = segmentIds.includes(segmentId)
      ? segmentIds.filter((selectedId) => selectedId !== segmentId)
      : [...segmentIds, segmentId];

    form.setValue('segmentIds', nextSegmentIds, { shouldDirty: true, shouldValidate: true });
  };

  const isBusy = isSaving || bannerUpload.isUploading || logoUpload.isUploading;

  const { requestClose, discardModalProps } = useDiscardChangesGuard({
    hasUnsavedChanges: form.formState.isDirty,
    onDiscard: onClose,
  });

  const handleRequestClose = () => {
    if (isBusy) {
      return;
    }

    requestClose();
  };

  const handleExited = () =>
    form.reset(buildDefaultValues(initialData), { keepDirtyValues: false });

  const save = form.handleSubmit((values) => {
    if (isBusy) {
      return;
    }

    if (!form.formState.isDirty) {
      onClose();
      return;
    }

    saveAbout({
      about: buildBrandAboutPayload(initialData?.googlePlusUrl, values),
      banner: form.formState.dirtyFields.bannerUrl ? { bannerUrl: values.bannerUrl } : undefined,
    });
  });

  const mediumPriceOptions = (WholesaleMediumPricesTypeEnum.values as AveragePriceOption[]).map(
    (item) => ({
      value: item.value,
      label: translate(item.label as LangEnum, {
        price: formatMoney(item.lowerValue),
        priceFrom: formatMoney(item.lowerValue),
        priceTo: formatMoney(item.biggerValue ?? 0),
      }),
    }),
  );

  return {
    control: form.control,
    mediumPriceOptions,
    bannerUrl,
    logoUrl,
    segmentIds,
    segmentOptions,
    segmentsError: form.formState.errors.segmentIds?.message,
    isSaving,
    isUploadingBanner: bannerUpload.isUploading,
    isUploadingLogo: logoUpload.isUploading,
    discardModalProps,
    save,
    handleChangeBanner,
    handleChangeLogo,
    handleToggleSegment,
    handleExited,
    handleRequestClose,
  };
};

export default useBrandAboutSectionForm;
